import axios from "axios";
import { BACKEND_URL } from "./config";

const TOKEN_EAGER_REFRESH_WINDOW_MS = 60_000;
const TOKEN_FALLBACK_TTL_MS = 30_000;
const TOKEN_MIN_REFRESH_BUFFER_MS = 5_000;

export async function resolveAuthToken(getToken, forceRefresh = false) {
  if (!getToken) return null;
  try {
    return forceRefresh ? await getToken(true) : await getToken();
  } catch {
    return null;
  }
}

const decodeJwtPayload = (token) => {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    const decoded =
      typeof atob === "function"
        ? atob(padded)
        : globalThis.Buffer.from(padded, "base64").toString("binary");
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

const computeTokenExpiry = (token) => {
  const payload = decodeJwtPayload(token);
  const expSeconds = payload?.exp;
  if (!expSeconds) return null;
  return expSeconds * 1000;
};

const isTokenFresh = (expiresAtMs) => {
  if (!expiresAtMs) return false;
  const ttlMs = expiresAtMs - Date.now();
  if (ttlMs <= 0) return false;
  const refreshBufferMs = Math.min(
    TOKEN_EAGER_REFRESH_WINDOW_MS,
    Math.max(TOKEN_MIN_REFRESH_BUFFER_MS, Math.floor(ttlMs / 2)),
  );
  return ttlMs > refreshBufferMs;
};

const createCachedTokenResolver = (getToken) => {
  const tokenCache = { value: null, expiresAt: 0 };
  let inFlightTokenRequest = null;

  return async (forceRefresh = false) => {
    if (!forceRefresh && tokenCache.value && isTokenFresh(tokenCache.expiresAt)) {
      return tokenCache.value;
    }

    if (inFlightTokenRequest) {
      return await inFlightTokenRequest;
    }

    inFlightTokenRequest = (async () => {
      const token = await resolveAuthToken(getToken, forceRefresh);
      if (!token) {
        tokenCache.value = null;
        tokenCache.expiresAt = 0;
        return null;
      }

      const expiresAt = computeTokenExpiry(token);
      tokenCache.value = token;
      tokenCache.expiresAt = expiresAt || Date.now() + TOKEN_FALLBACK_TTL_MS;
      return token;
    })();

    try {
      return await inFlightTokenRequest;
    } finally {
      inFlightTokenRequest = null;
    }
  };
};

export function createAuthFetch(getToken) {
  const getCachedToken = createCachedTokenResolver(getToken);
  return async (url, options = {}) => {
    const { throwOnError = true, ...fetchOptions } = options;
    const token = await getCachedToken(false);
    const headers = {
      ...(fetchOptions.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const response = await fetch(url, { ...fetchOptions, headers });
    if (!response.ok && throwOnError) {
      const error = new Error(`Request failed with status ${response.status}`);
      error.response = response;
      throw error;
    }
    return response;
  };
}

export function makeApiClient(getToken, baseURL = BACKEND_URL) {
  const getCachedToken = createCachedTokenResolver(getToken);

  const api = axios.create({
    baseURL,
    withCredentials: true,
    timeout: 3 * 60 * 1000,
  });

  api.interceptors.request.use(async (config) => {
    const token = await getCachedToken(false);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  api.interceptors.response.use(
    (res) => res,
    async (err) => {
      const config = err.config;
      if (err.response?.status === 401 && getToken && config && !config._retry) {
        config._retry = true;
        try {
          const token = await getCachedToken(true);
          if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
            return api.request(config);
          }
        } catch {
          /* fall through */
        }
      }
      return Promise.reject(err);
    },
  );

  return api;
}
